<template>
	<div
		class="recently-completed"
		data-test="recently-completed"
	>
		<div class="recently-completed__header">
			<h3 class="recently-completed__title sgnl-text-color-default sgnl-paragraph-l-medium">
				Recently Completed
			</h3>
			<v-btn
				variant="text"
				size="small"
				to="/tasks"
				class="bento-tile__action"
			>
				Builds and Tasks
				<ArrowRight16 />
			</v-btn>
		</div>

		<SgnlTable
			:columns="columns"
			:data="rows"
			:loading="loading"
			:row-key="rowKeyFn"
			:row-class="rowClassFn"
			:row-attrs="rowAttrsFn"
			@row-click="onRowClick"
		>
			<!-- Tailored loading skeleton -->
			<template #loading>
				<tr
					v-for="i in SKELETON_ROW_COUNT"
					:key="i"
					class="recently-completed__row recently-completed__row--skeleton"
				>
					<td class="col-id">
						<SkeletonBlock
							width="var(--sgnl-unit-64)"
							height="var(--sgnl-unit-20)"
							radius="var(--sgnl-border-radius-4)"
						/>
					</td>
					<td class="col-core">
						<SkeletonBlock
							width="var(--sgnl-unit-72)"
							height="var(--sgnl-unit-24)"
							radius="var(--sgnl-border-radius-6)"
						/>
					</td>
					<td class="col-repo">
						<SkeletonBlock
							class="sgnl-caption-regular"
							width="80%"
							radius="var(--sgnl-border-radius-4)"
						/>
					</td>
					<td class="col-started">
						<SkeletonBlock
							class="sgnl-caption-regular"
							width="var(--sgnl-unit-72)"
							radius="var(--sgnl-border-radius-4)"
						/>
					</td>
					<td class="col-duration">
						<SkeletonBlock
							class="sgnl-caption-regular"
							width="var(--sgnl-unit-48)"
							radius="var(--sgnl-border-radius-4)"
						/>
					</td>
					<td class="col-status">
						<SkeletonBlock
							class="sgnl-caption-regular"
							width="var(--sgnl-unit-72)"
							radius="var(--sgnl-border-radius-4)"
						/>
					</td>
					<td class="col-actions">
						<SkeletonBlock
							width="var(--sgnl-unit-56)"
							height="var(--sgnl-unit-20)"
							radius="var(--sgnl-border-radius-4)"
						/>
					</td>
				</tr>
			</template>

			<!-- Empty state -->
			<template #empty>
				<EmptyState
					header="No tasks yet"
					body="Connect a repository or use the Code Playground to run your first task. Recent runs will appear here."
				>
					<template #actions>
						<v-btn
							variant="outlined"
							:to="'/repositories'"
						>
							<LogoGithub16 />
							Connect repository
						</v-btn>
						<v-btn
							variant="flat"
							color="primary"
							:to="{ name: 'CodeEditor' }"
						>
							<Play16 />
							Open Code Playground
						</v-btn>
					</template>
				</EmptyState>
			</template>

			<!-- ID -->
			<template #cell-id="{ row }">
				<span class="recently-completed__cell-id-inner">
					<span class="recently-completed__id-text sgnl-caption-regular">{{ row.idShort }}</span>
					<button
						type="button"
						class="recently-completed__copy-btn"
						aria-label="Copy ID"
						:title="copiedId === row.id ? 'Copied' : 'Copy ID'"
						data-test="recently-completed-copy-id"
						@click.stop="copyId(row.id)"
					>
						<Transition
							name="copy-icon-swap"
							mode="out-in"
						>
							<CheckmarkFilled16
								v-if="copiedId === row.id"
								key="check"
								class="recently-completed__copy-icon recently-completed__copy-icon--copied"
							/>
							<Copy16
								v-else
								key="copy"
								class="recently-completed__copy-icon"
							/>
						</Transition>
					</button>
				</span>
			</template>

			<!-- Core -->
			<template #cell-core="{ row }">
				<span class="recently-completed__chip sgnl-text-color-default sgnl-caption-regular">{{
					row.core
				}}</span>
			</template>

			<!-- Repository -->
			<template #cell-repo="{ row }">
				<span
					class="recently-completed__cell-repo sgnl-caption-regular sgnl-text-color-default"
					:title="row.repo"
					>{{ row.repo }}</span
				>
			</template>

			<!-- Started -->
			<template #cell-started="{ row }">
				<span class="sgnl-caption-regular sgnl-text-color-secondary">{{ row.startedLabel }}</span>
			</template>

			<!-- Duration -->
			<template #cell-duration="{ row }">
				<span class="recently-completed__cell-mono sgnl-caption-regular sgnl-text-color-secondary">{{
					row.duration
				}}</span>
			</template>

			<!-- Status -->
			<template #cell-status="{ row }">
				<span class="recently-completed__status sgnl-caption-regular">
					<span
						class="recently-completed__dot"
						:class="`recently-completed__dot--${row.tone}`"
					/>
					<span>{{ row.status }}</span>
					<WarningAlt16
						v-if="row.failed"
						class="recently-completed__status-fail-icon"
					/>
				</span>
			</template>

			<!-- Actions -->
			<template #cell-actions="{ row }">
				<v-btn
					v-if="row.kind === 'task'"
					variant="text"
					size="small"
					:to="`/tasks/${row.id}`"
					class="bento-tile__action"
					@click.stop
				>
					View
					<ArrowRight16 />
				</v-btn>
			</template>

			<!-- Mobile card -->
			<template #card="{ row }">
				<div class="recently-completed__card-top">
					<span class="recently-completed__id-text sgnl-caption-regular">{{ row.idShort }}</span>
					<span class="recently-completed__status sgnl-caption-regular">
						<span
							class="recently-completed__dot"
							:class="`recently-completed__dot--${row.tone}`"
						/>
						<span>{{ row.status }}</span>
					</span>
				</div>
				<div class="recently-completed__card-mid sgnl-text-color-default sgnl-caption-regular">
					<span class="recently-completed__chip sgnl-text-color-default">{{ row.core }}</span>
					<span class="recently-completed__cell-repo">{{ row.repo }}</span>
				</div>
				<div class="recently-completed__card-bottom sgnl-text-color-secondary sgnl-footnote-regular">
					<span>{{ row.startedLabel }} · {{ row.duration }}</span>
					<router-link
						v-if="row.kind === 'task'"
						:to="`/tasks/${row.id}`"
						class="recently-completed__view sgnl-text-color-brand sgnl-caption-medium"
						@click.stop
					>
						View <ArrowRight16 />
					</router-link>
				</div>
			</template>
		</SgnlTable>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import moment from "moment";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";
import Copy16 from "@carbon/icons-vue/es/copy/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark/16";
import WarningAlt16 from "@carbon/icons-vue/es/warning--alt/16";
import LogoGithub16 from "@carbon/icons-vue/es/logo--github/16";
import Play16 from "@carbon/icons-vue/es/play/16";

import SgnlTable from "@/components/Common/Table.vue";
import EmptyState from "@/components/Common/EmptyState.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";

import { mapState } from "pinia";
import { useBuildsStore } from "@/stores/builds";
import { useTasksStore } from "@/stores/tasks";
import { useCoresStore } from "@/stores/cores";
import { pushFeedback } from "@/composables/feedbackQueue";
import type { Build } from "@/types/api/builds";
import { BuildStatusE, isBuildActive } from "@/types/api/builds";
import type { Task } from "@/types/api/tasks";
import { TaskStatusE, isTaskActive } from "@/types/api/tasks";
import type { Core } from "@/types/api/cores";
import type { ColumnDef } from "@tanstack/vue-table";
import {
	convertToMilliseconds,
	buildStatusColor,
	taskStatusColor,
	coreNameFromSpecs,
	repoShortNameFromUrl,
	shortenID,
} from "@/js/utilities";

type Tone = "success" | "info" | "warning" | "error" | "secondary";

const VUETIFY_TONE_TO_DOT: Record<string, Tone> = {
	success: "success",
	info: "info",
	warning: "warning",
	error: "error",
	secondary: "secondary",
};

type Row = {
	kind: "build" | "task";
	id: string;
	idShort: string;
	core: string;
	repo: string;
	createdMs: number;
	finishedMs: number | null;
	startedLabel: string;
	duration: string;
	status: string;
	tone: Tone;
	failed: boolean;
};

const FRESH_FINISH_WINDOW_MS = 5_000;
const SKELETON_ROW_COUNT = 6;

// Content is rendered through per-column slots, so columns carry no accessor and never sort.
const RC_COLUMNS: ColumnDef<Row>[] = [
	{ id: "id", header: "ID", enableSorting: false },
	{ id: "core", header: "Core", enableSorting: false },
	{ id: "repo", header: "Repository", enableSorting: false },
	{ id: "started", header: "Started", enableSorting: false },
	{ id: "duration", header: "Duration", enableSorting: false },
	{ id: "status", header: "Status", enableSorting: false },
	{ id: "actions", header: "Actions", enableSorting: false },
];

const formatDuration = (startMs: number | null, finishedMs: number | null): string => {
	if (!startMs || !finishedMs) return "—";
	const duration = finishedMs - startMs;
	if (duration < 1000) return `${duration}ms`;
	if (duration < 60_000) {
		const s = Math.floor(duration / 1000);
		const ms = duration % 1000;
		return ms > 0 ? `${s}s ${ms}ms` : `${s}s`;
	}
	const m = Math.floor(duration / 60_000);
	const s = Math.floor((duration % 60_000) / 1000);
	return `${m}m ${s}s`;
};

const repoNameFromBuild = (b: Build | undefined): string => {
	if (!b) return "—";
	const app = b.Application;
	if (app.Type === "Repository") {
		return repoShortNameFromUrl(app.Repository?.RemoteURL) || "—";
	}
	if (app.Type === "SourceCode") return "Code Playground";
	return "—";
};

const buildTone = (b: Build): Tone => {
	return VUETIFY_TONE_TO_DOT[buildStatusColor(b.Status)] ?? "secondary";
};

const taskTone = (t: Task): Tone => {
	return VUETIFY_TONE_TO_DOT[taskStatusColor(t.Status)] ?? "secondary";
};

export default defineComponent({
	name: "RecentlyCompletedTable",
	components: {
		ArrowRight16,
		Copy16,
		CheckmarkFilled16,
		WarningAlt16,
		LogoGithub16,
		Play16,
		SgnlTable,
		EmptyState,
		SkeletonBlock,
	},
	data: () => ({
		copiedId: "" as string,
		copyTimer: undefined as undefined | ReturnType<typeof setTimeout>,
		SKELETON_ROW_COUNT,
	}),
	computed: {
		...mapState(useBuildsStore, {
			buildHistory: "buildHistory",
			buildHistoryUpdateStatus: "buildHistoryUpdateStatus",
		}),
		...mapState(useTasksStore, { taskHistory: "taskHistory", taskHistoryUpdateStatus: "taskHistoryUpdateStatus" }),
		...mapState(useCoresStore, { defaultCores: "allDefaultCores" }),
		columns(): ColumnDef<Row>[] {
			return RC_COLUMNS;
		},
		loading(): boolean {
			const buildsLoading = this.buildHistoryUpdateStatus.loading && (this.buildHistory ?? []).length === 0;
			const tasksLoading = this.taskHistoryUpdateStatus.loading && (this.taskHistory ?? []).length === 0;
			return buildsLoading || tasksLoading;
		},
		rows(): Row[] {
			const defaults = (this.defaultCores as Core[] | undefined) ?? [];
			const builds = ((this.buildHistory as Build[] | undefined) ?? []).filter((b) => !isBuildActive(b.Status));
			const tasks = ((this.taskHistory as Task[] | undefined) ?? []).filter((t) => !isTaskActive(t.Status));

			const buildRows: Row[] = builds.map((b) => {
				const startedMs = b.StartedAt ? convertToMilliseconds(b.StartedAt) : null;
				const finishedMs = b.FinishedAt ? convertToMilliseconds(b.FinishedAt) : null;
				const createdMs = b.CreatedAt ? convertToMilliseconds(b.CreatedAt) : 0;
				return {
					kind: "build",
					id: b.BuildID,
					idShort: shortenID(b.BuildID),
					core: coreNameFromSpecs(b.BuildCoreSpecs, defaults, "—"),
					repo: repoNameFromBuild(b),
					createdMs,
					finishedMs,
					startedLabel: startedMs ? moment(startedMs).fromNow() : "—",
					duration: formatDuration(startedMs, finishedMs),
					status: b.Status,
					tone: buildTone(b),
					failed: b.Status === BuildStatusE.Error,
				};
			});

			const buildById = new Map(builds.map((b) => [b.BuildID, b]));
			const taskRows: Row[] = tasks.map((t) => {
				const startedMs = t.StartedAt ? convertToMilliseconds(t.StartedAt) : null;
				const finishedMs = t.FinishedAt ? convertToMilliseconds(t.FinishedAt) : null;
				const createdMs = t.CreatedAt ? convertToMilliseconds(t.CreatedAt) : 0;
				const parent = buildById.get(t.BuildID);
				return {
					kind: "task",
					id: t.TaskID,
					idShort: shortenID(t.TaskID),
					core: coreNameFromSpecs(parent?.BuildCoreSpecs, defaults, "—"),
					repo: repoNameFromBuild(parent),
					createdMs,
					finishedMs,
					startedLabel: startedMs ? moment(startedMs).fromNow() : "—",
					duration: formatDuration(startedMs, finishedMs),
					status: t.Status,
					tone: taskTone(t),
					failed: t.Status === TaskStatusE.Error,
				};
			});

			return [...taskRows, ...buildRows].sort((a, b) => b.createdMs - a.createdMs).slice(0, 6);
		},
	},
	methods: {
		rowKeyFn(row: Row): string {
			return `${row.kind}:${row.id}`;
		},
		rowClassFn(row: Row): (string | Record<string, boolean>)[] {
			return [
				"recently-completed__row",
				{
					"recently-completed__row--failed": row.failed,
					"recently-completed__row--clickable": row.kind === "task",
					"recently-completed__row--fresh": this.isFresh(row),
				},
			];
		},
		rowAttrsFn(row: Row): Record<string, string> {
			return { "data-test": `recently-completed-row-${row.kind}` };
		},
		onRowClick(row: Row) {
			if (row.kind === "task") this.openTask(row.id);
		},
		async copyId(id: string) {
			if (!navigator?.clipboard) return;
			try {
				await navigator.clipboard.writeText(id);
				this.copiedId = id;
				pushFeedback("ID copied", "success");
				if (this.copyTimer) clearTimeout(this.copyTimer);
				this.copyTimer = setTimeout(() => {
					this.copiedId = "";
				}, 1500);
			} catch {
				pushFeedback("Copy ID failed. Please try again.", "error");
			}
		},
		openTask(id: string) {
			this.$router.push(`/tasks/${id}`);
		},
		isFresh(row: Row): boolean {
			return row.finishedMs !== null && Date.now() - row.finishedMs < FRESH_FINISH_WINDOW_MS;
		},
	},
	beforeUnmount() {
		if (this.copyTimer) clearTimeout(this.copyTimer);
	},
});
</script>

<style lang="scss" scoped>
.recently-completed {
	background: var(--sgnl-default-primary-background);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	border-radius: var(--sgnl-border-radius-6);
	overflow: hidden;
}

.recently-completed__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--sgnl-space-12);
	padding: var(--sgnl-space-12) var(--sgnl-space-16);
	flex-wrap: wrap;
}

.recently-completed__title {
	margin: 0;
}

/* === Table-wrapper skin overrides (scoped + outer-container-prefixed to beat the wrapper's own rules) === */
/* The outer container owns the border, so neutralize the wrapper frame. */
.recently-completed :deep(.sgnl-table__frame) {
	border: none;
	border-radius: 0;
}

/* Content-driven column widths (id/actions shrink to fit) instead of the wrapper's fixed layout. */
.recently-completed :deep(.sgnl-data-table) {
	table-layout: auto;
}

.recently-completed :deep(.sgnl-data-table thead tr) {
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
}

.recently-completed :deep(.sgnl-data-table tbody td) {
	height: auto;
	/* Only override vertical padding so per-column padding-left/right rules still apply. */
	padding-top: var(--sgnl-space-10);
	padding-bottom: var(--sgnl-space-10);
	line-height: var(--sgnl-unit-20);
	white-space: nowrap;
}

.recently-completed :deep(.col-id) {
	font-family: var(--sgnl-font-family-mono);
	width: 1px;
	padding-right: var(--sgnl-space-8);
}

.recently-completed :deep(.col-actions) {
	width: 1px;
	text-align: left;
}

.recently-completed :deep(.recently-completed__row--clickable) {
	cursor: pointer;
}

/* Only task rows are interactive, so limit the wrapper's row hover to them. */
.recently-completed :deep(.sgnl-data-table tbody tr:hover) {
	background: transparent;
}

.recently-completed :deep(.sgnl-data-table tbody tr.recently-completed__row--clickable:hover) {
	background: var(--sgnl-default-background-hover);
}

.recently-completed :deep(.recently-completed__row--fresh) {
	animation: recently-completed-fresh 5s ease-out;
}

.recently-completed :deep(.recently-completed__row:hover .recently-completed__copy-btn) {
	opacity: 1;
}

@keyframes recently-completed-fresh {
	0%,
	30% {
		background: var(--sgnl-brand-background-inverse);
	}
	100% {
		background: transparent;
	}
}

/* Skeleton rows are slotted (consumer-scoped), so they need their own row separators. */
.recently-completed__row--skeleton td {
	padding: var(--sgnl-space-10) var(--sgnl-space-16);
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-tertiary-border);
}

/* === Cell content (slot scope) === */
.recently-completed__cell-id-inner {
	display: inline-flex;
	align-items: center;
	gap: var(--sgnl-space-6);
	vertical-align: middle;
}

.recently-completed__id-text {
	font-family: var(--sgnl-font-family-mono);
	display: inline-block;
	line-height: var(--sgnl-unit-20);
}

.recently-completed__copy-btn {
	opacity: 0;
	transition:
		opacity var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard),
		transform var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard);
	background: transparent;
	border: none;
	padding: 0;
	margin: 0;
	cursor: pointer;
	color: var(--sgnl-default-secondary-icon);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	line-height: 0;
	vertical-align: middle;
}

.recently-completed__copy-btn:active {
	transform: scale(0.92);
}

.recently-completed__copy-icon--copied {
	color: var(--sgnl-brand-icon);
	opacity: 1;
}

.copy-icon-swap-leave-active {
	transition:
		filter var(--sgnl-motion-duration-fast-01) var(--sgnl-productive-effect-exit),
		opacity var(--sgnl-motion-duration-fast-01) var(--sgnl-productive-effect-exit);
}
.copy-icon-swap-enter-active {
	transition:
		transform var(--sgnl-motion-duration-moderate-02) var(--sgnl-expressive-effect-entrance),
		opacity var(--sgnl-motion-duration-moderate-01) var(--sgnl-expressive-effect-entrance);
}
.copy-icon-swap-leave-to {
	filter: blur(2px);
	opacity: 0;
}
.copy-icon-swap-enter-from {
	transform: scale(0.7);
	opacity: 0;
}

.recently-completed__chip {
	display: inline-flex;
	align-items: center;
	height: var(--sgnl-unit-24);
	padding: 0 var(--sgnl-space-8);
	border-radius: var(--sgnl-border-radius-6);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	font-family: var(--sgnl-font-family-mono);
}

.recently-completed__view {
	display: inline-flex;
	align-items: center;
	gap: var(--sgnl-space-4);
	text-decoration: none;
	padding: var(--sgnl-space-4) var(--sgnl-space-8);
	border-radius: var(--sgnl-border-radius-6);
}

.recently-completed__view:hover {
	background: var(--sgnl-brand-background-inverse);
}

.recently-completed__cell-repo {
	display: block;
	max-width: var(--sgnl-unit-256);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.recently-completed__cell-mono {
	font-family: var(--sgnl-font-family-mono);
}

.recently-completed__status {
	display: inline-flex;
	align-items: center;
	gap: var(--sgnl-space-8);
}

.recently-completed__dot {
	width: var(--sgnl-unit-8);
	height: var(--sgnl-unit-8);
	border-radius: 50%;
}
.recently-completed__dot--success {
	background: var(--sgnl-brand-background);
}
.recently-completed__dot--info {
	background: var(--sgnl-alert-info-border);
}
.recently-completed__dot--warning {
	background: var(--sgnl-alert-warning-border);
}
.recently-completed__dot--error {
	background: var(--sgnl-alert-error-border);
}
.recently-completed__dot--secondary {
	background: var(--sgnl-default-secondary-icon);
}

.recently-completed__status-fail-icon {
	color: var(--sgnl-alert-error-icon);
}

/* === Mobile card sub-layout === */
.recently-completed :deep(.sgnl-table__card) {
	padding: var(--sgnl-space-12) var(--sgnl-space-20);
	gap: var(--sgnl-space-10);
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	border-bottom: none;
}

.recently-completed__card-top,
.recently-completed__card-bottom {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: var(--sgnl-space-8);
}

.recently-completed__card-mid {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: var(--sgnl-space-8);
}

@media (max-width: 1099px) {
	.recently-completed :deep(.col-duration) {
		display: none;
	}
}

@media (max-width: 899px) {
	.recently-completed :deep(.col-started) {
		display: none;
	}
	.recently-completed__cell-repo {
		max-width: var(--sgnl-unit-128);
	}
}
</style>
